import React, { useEffect, useState, useCallback, memo, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { MapPin, Phone, Mail, Clock, MessageCircle, Instagram, Facebook, Hash } from "lucide-react";
import { bakeryInfo, products, aboutContent, categories } from "../mock";
import OptimizedImage from "./OptimizedImage";

// Memoized components for better performance
const ProductCard = memo(({ product }) => (
  <Card className="product-card">
    <div className="product-image-container">
      <OptimizedImage
        src={product.image}
        alt={product.name}
        className="product-image"
        width={400}
        height={300}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        loading="lazy"
      />
      <div className="product-category-tag">
        {product.category}
      </div>
    </div>
    <CardContent className="product-info">
      <h3 className="product-name">{product.name}</h3>
      <p className="product-description">{product.description}</p>
      <p className="product-price">{product.price}</p>
    </CardContent>
  </Card>
));

const ValueCard = memo(({ value, index }) => (
  <Card key={index} className="value-card">
    <CardContent>
      <h3 className="value-title">{value.title}</h3>
      <p className="value-description">{value.description}</p>
    </CardContent>
  </Card>
));

const TimelineItem = memo(({ item, index }) => (
  <div key={index} className="timeline-item">
    <div className="timeline-content">
      <h4 className="timeline-item-title">{item.title}</h4>
      <p className="timeline-item-description">{item.description}</p>
    </div>
    <div className="timeline-year">{item.year}</div>
  </div>
));

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [email, setEmail] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('home');

  const observer = useRef(null);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (Math.abs(currentScrollY - scrollY) > 5) { // Only update if significant change
      setScrollY(currentScrollY);
    }
  }, [scrollY]);

  useEffect(() => {
    
    window.addEventListener('scroll', handleScroll);

    const sections = document.querySelectorAll('section[id]');

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    observer.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveLink(entry.target.id);
        }
      });
    }, observerOptions);

    sections.forEach(section => {
      observer.current.observe(section);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
    
    // Throttled scroll event listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [handleScroll]);

  const handleNavClick = (section) => {
    scrollToSection(section);
    setActiveLink(section);  
    setIsMenuOpen(false);    
  };


  // Memoized filter function
  const filterProducts = useCallback((category) => {
    if (category === 'all') {
      return products;
    }
    return products.filter(product => product.category === category);
  }, []);

  useEffect(() => {
    setFilteredProducts(filterProducts(activeCategory));
  }, [activeCategory, filterProducts]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleWhatsAppClick = useCallback(() => {
    const message = encodeURIComponent(`Halo Ubee Bakery! Saya tertarik untuk memesan produk bakery Anda.`);
    window.open(`https://wa.me/${bakeryInfo.contact.whatsapp}?text=${message}`, '_blank', 'noopener');
  }, []);

  const handleNewsletterSubmit = useCallback((e) => {
    e.preventDefault();
    // Mock newsletter subscription with better UX
    if (email) {
      alert(`Terima kasih! ${email} telah berlangganan newsletter Ubee Bakery.`);
      setEmail('');
    }
  }, [email]);

  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
  }, []);

  // Optimized parallax calculation
  const parallaxOffset = Math.min(scrollY * 0.5, 300); // Cap the parallax effect

  return (
    <div className="landing-page">
      {/* Enhanced Navigation */}
      <nav className={`navbar ${scrollY > 50 ? 'scrolled' : ''}`}>
  <a href="#home" className="logo" onClick={() => handleNavClick('home')}>
    {bakeryInfo.name}
  </a>

  <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
    <li>
      <a 
        href="#home" 
        className={`nav-link ${activeLink === 'home' ? 'active' : ''}`}
        onClick={() => handleNavClick('home')}
      >
        Home
      </a>
    </li>
    <li>
      <a 
        href="#products" 
        className={`nav-link ${activeLink === 'products' ? 'active' : ''}`}
        onClick={() => handleNavClick('products')}
      >
        Products
      </a>
    </li>
    <li>
      <a 
        href="#about" 
        className={`nav-link ${activeLink === 'about' ? 'active' : ''}`}
        onClick={() => handleNavClick('about')}
      >
        About
      </a>
    </li>
    <li>
      <a 
        href="#location" 
        className={`nav-link ${activeLink === 'location' ? 'active' : ''}`}
        onClick={() => handleNavClick('location')}
      >
        Location
      </a>
    </li>
  </ul>

  <button
    className={`nav-toggle ${isMenuOpen ? 'active' : ''}`}
    aria-label="toggle navigation"
    onClick={() => setIsMenuOpen(!isMenuOpen)}
  >
    <span className="hamburger"></span>
  </button>
</nav>

      {/* Enhanced Hero Section with Optimized Parallax */}
      <section id="home" className="hero-section">
        <OptimizedImage
          src={bakeryInfo.hero.backgroundImage}
          alt="Ubee Bakery Interior"
          className="hero-parallax"
          width={1920}
          height={1080}
          priority={true}
          loading="eager"
          placeholder={false}
          style={{
            transform: `translate3d(0, ${parallaxOffset}px, 0)`
          }}
        />
        <div className="hero-overlay"></div>
        <div className={`hero-content ${isVisible ? 'fade-in-up' : ''}`}>
          <h1 className="hero-title">{bakeryInfo.hero.title}</h1>
          <p className="hero-subtitle">{bakeryInfo.hero.subtitle}</p>
          <p className="hero-description">{bakeryInfo.hero.description}</p>
          <div className="hero-buttons">
            <Button 
              className="cta-button-primary"
              onClick={() => scrollToSection('products')}
            >
              Lihat Produk
            </Button>
            <Button 
              className="cta-button-secondary"
              onClick={() => scrollToSection('location')}
            >
              <MapPin size={20} />
              Kunjungi Toko
            </Button>
          </div>
        </div>
      </section>

      {/* Enhanced Products Section with Optimized Filtering */}
      <section id="products" className="section">
        <div className="section-header">
          <h2 className="section-title">Produk Unggulan</h2>
          <p className="section-subtitle">Pilihan terbaik dari dapur kami yang dibuat dengan cinta setiap hari</p>
        </div>
        
        {/* Category Filters */}
        <div className="product-filters">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`filter-button ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
        
        <div className="products-grid">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* Enhanced About Section with Optimized Images */}
      <section id="about" className="section">
        <div className="section-header">
          <h2 className="section-title">{aboutContent.title}</h2>
          <p className="section-subtitle">{aboutContent.subtitle}</p>
        </div>
        
        <div className="about-content">
          {/* Story Section with Optimized Images */}
          <div className="about-story">
            <div className="about-text-content">
              <p className="contact-item about-text">{aboutContent.story}</p>
              <p className="contact-item about-text">{aboutContent.mission}</p>
            </div>
            <div className="about-images">
              <OptimizedImage
                src={aboutContent.teamImage}
                alt="Tim Ubee Bakery"
                className="about-image"
                width={400}
                height={300}
                loading="lazy"
              />
              <OptimizedImage
                src={aboutContent.workImage}
                alt="Bakers at work"
                className="about-image"
                width={400}
                height={300}
                loading="lazy"
              />
              <OptimizedImage
                src={aboutContent.interiorImage}
                alt="Interior bakery"
                className="about-image large"
                width={800}
                height={400}
                loading="lazy"
              />
            </div>
          </div>

          {/* Timeline Section */}
          <div className="timeline-container">
            <h3 className="timeline-title">Perjalanan Kami</h3>
            <div className="timeline">
              {aboutContent.timeline.map((item, index) => (
                <TimelineItem key={index} item={item} index={index} />
              ))}
            </div>
          </div>
          
          {/* Values Grid */}
          <div className="values-grid">
            {aboutContent.values.map((value, index) => (
              <ValueCard key={index} value={value} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Location Section */}
      <section id="location" className="section">
        <div className="section-header">
          <h2 className="section-title">Kunjungi Kami</h2>
          <p className="section-subtitle">Temukan lokasi dan kontak kami untuk pengalaman bakery terbaik</p>
        </div>
        
        <div className="location-content">
          <div className="contact-info">
            <div className="contact-item">
              <MapPin className="contact-icon" size={24} />
              <div>
                <div className="contact-text">{bakeryInfo.contact.address}</div>
              </div>
            </div>
            
            <div className="contact-item">
              <Phone className="contact-icon" size={24} />
              <div>
                <div className="contact-text">{bakeryInfo.contact.phone}</div>
              </div>
            </div>
            
            <div className="contact-item">
              <Mail className="contact-icon" size={24} />
              <div>
                <div className="contact-text">{bakeryInfo.contact.email}</div>
              </div>
            </div>
            
            <div className="contact-item">
              <Clock className="contact-icon" size={24} />
              <div>
                <div className="contact-text">
                  <div><strong>Senin - Jumat:</strong> {bakeryInfo.contact.hours.weekdays}</div>
                  <div><strong>Sabtu - Minggu:</strong> {bakeryInfo.contact.hours.weekend}</div>
                </div>
              </div>
            </div>

            <button className="whatsapp-button" onClick={handleWhatsAppClick}>
              <MessageCircle size={20} />
              Pesan Sekarang via WhatsApp
            </button>
          </div>
          
          <div className="map-container">
            <iframe
              src={bakeryInfo.contact.mapEmbedUrl}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubee Bakery Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">{bakeryInfo.name}</div>
            <div className="footer-motto">{bakeryInfo.motto}</div>
            <p className="footer-description">
              Menghadirkan kelezatan dan kelembutan dalam setiap gigitan. 
              Kualitas terbaik dengan cita rasa yang tak terlupakan.
            </p>
            <div className="social-links">
              <a href={bakeryInfo.social.instagram} className="social-link" target="_blank" rel="noopener noreferrer">
                <Instagram size={20} />
              </a>
              <a href={bakeryInfo.social.facebook} className="social-link" target="_blank" rel="noopener noreferrer">
                <Facebook size={20} />
              </a>
              <a href={bakeryInfo.social.tiktok} className="social-link" target="_blank" rel="noopener noreferrer">
                <Hash size={20} />
              </a>
            </div>
          </div>
          
          <div className="footer-section">
            <h3>Jam Operasional</h3>
            <p><strong>Senin - Jumat</strong><br/>{bakeryInfo.contact.hours.weekdays}</p>
            <p><strong>Sabtu - Minggu</strong><br/>{bakeryInfo.contact.hours.weekend}</p>
          </div>
          
          <div className="footer-section">
            <h3>Newsletter</h3>
            <p>Dapatkan info promo dan produk terbaru</p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                className="newsletter-input"
                placeholder="Email Anda"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <button type="submit" className="newsletter-button">
                Berlangganan
              </button>
            </form>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>© 2024 {bakeryInfo.name}. All rights reserved. | Made with ❤️ for our beloved customers</p>
        </div>
      </footer>
    </div>
  );
};

export default memo(LandingPage);